// src/components/projects/ProjectSettings.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../hooks/useAuth";
import { useNotification } from "../../contexts/NotificationContext";
import Button from "../common/Button";
import Input from "../common/Input";
import Select from "../common/Select";
import Loading from "../common/Loading";

const ProjectSettings = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { notify } = useNotification();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newMemberId, setNewMemberId] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    key: "",
    emoji: "📁",
  });

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectDoc = await getDoc(doc(db, "projects", projectId));

        if (!projectDoc.exists()) {
          notify.error("Project not found");
          navigate("/projects");
          return;
        }

        const projectData = {
          id: projectDoc.id,
          ...projectDoc.data(),
        };

        // Check permissions
        const userIsMember = projectData.members?.includes(userProfile?.uid);
        const userIsAdmin = userProfile?.role === "admin";
        const userIsManager = userProfile?.role === "manager";

        if (!userIsMember && !userIsAdmin && !userIsManager) {
          notify.error("You do not have access to this project");
          navigate("/projects");
          return;
        }

        setProject(projectData);
        setFormData({
          name: projectData.name,
          description: projectData.description,
          key: projectData.key,
          emoji: projectData.emoji,
        });

        // Fetch project members details
        await fetchProjectMembers(projectData.members);
      } catch (error) {
        console.error("Error fetching project:", error);
        notify.error("Failed to load project");
        navigate("/projects");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && userProfile) {
      fetchProject();
    }
  }, [projectId, navigate, notify, userProfile]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const usersQuery = query(collection(db, "users"));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
        notify.error("Failed to load users");
      }
    };

    fetchAllUsers();
  }, [notify]);

  const fetchProjectMembers = async (memberIds) => {
    if (!memberIds || memberIds.length === 0) {
      setUsers([]);
      return;
    }

    try {
      const usersQuery = query(
        collection(db, "users"),
        where("uid", "in", memberIds)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching project members:", error);
      notify.error("Failed to load project members");
    }
  };

  const emojiOptions = [
    { value: "📁", label: "📁 Folder" },
    { value: "🚀", label: "🚀 Rocket" },
    { value: "💻", label: "💻 Computer" },
    { value: "📱", label: "📱 Mobile" },
    { value: "🌐", label: "🌐 Web" },
    { value: "🔧", label: "🔧 Tool" },
    { value: "📊", label: "📊 Chart" },
    { value: "🎯", label: "🎯 Target" },
  ];

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.key) {
      notify.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);

      await updateDoc(doc(db, "projects", projectId), {
        ...formData,
        updatedAt: new Date().toISOString(),
      });

      setProject((prev) => ({
        ...prev,
        ...formData,
      }));

      notify.success("Project updated successfully");
    } catch (error) {
      console.error("Error updating project:", error);
      notify.error("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberId) {
      notify.error("Please select a user to add");
      return;
    }

    try {
      setAddingMember(true);

      // Find the selected user
      const userToAdd = allUsers.find((user) => user.uid === newMemberId);

      if (!userToAdd) {
        notify.error("Selected user not found");
        return;
      }

      // Check if user is already a member
      if (project.members.includes(userToAdd.uid)) {
        notify.error("User is already a member of this project");
        return;
      }

      // Add user to project members
      const updatedMembers = [...project.members, userToAdd.uid];
      await updateDoc(doc(db, "projects", projectId), {
        members: updatedMembers,
        updatedAt: new Date().toISOString(),
      });

      // Update local state
      setProject((prev) => ({
        ...prev,
        members: updatedMembers,
      }));

      // Refresh members list
      await fetchProjectMembers(updatedMembers);

      setNewMemberId("");
      notify.success(
        `Added ${userToAdd.displayName || userToAdd.email} to project`
      );
    } catch (error) {
      console.error("Error adding member:", error);
      notify.error("Failed to add member to project");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (memberId === userProfile?.uid) {
      notify.error("You cannot remove yourself from the project");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to remove ${memberName} from this project?`
      )
    ) {
      return;
    }

    try {
      const updatedMembers = project.members.filter((id) => id !== memberId);
      await updateDoc(doc(db, "projects", projectId), {
        members: updatedMembers,
        updatedAt: new Date().toISOString(),
      });

      // Update local state
      setProject((prev) => ({
        ...prev,
        members: updatedMembers,
      }));

      // Refresh members list
      await fetchProjectMembers(updatedMembers);

      notify.success(`Removed ${memberName} from project`);
    } catch (error) {
      console.error("Error removing member:", error);
      notify.error("Failed to remove member from project");
    }
  };

  const handleDeleteProject = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${project.name}"? This will also delete all associated issues and cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setDeleting(true);
      await deleteDoc(doc(db, "projects", projectId));
      notify.success("Project deleted successfully");
      navigate("/projects");
    } catch (error) {
      console.error("Error deleting project:", error);
      notify.error("Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  // Avatar component with fallback
  const UserAvatar = ({ user, size = 8 }) => {
    const [imageError, setImageError] = useState(false);

    const getInitials = (name, email) => {
      if (name) {
        return name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);
      }
      return email ? email[0].toUpperCase() : "?";
    };

    const getAvatarColor = (identifier) => {
      if (!identifier) return "bg-blue-500";

      let hash = 0;
      for (let i = 0; i < identifier.length; i++) {
        hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
      }

      const colors = [
        "bg-blue-500",
        "bg-green-500",
        "bg-purple-500",
        "bg-indigo-500",
        "bg-pink-500",
        "bg-teal-500",
      ];

      const index = Math.abs(hash) % colors.length;
      return colors[index];
    };

    // Use avatarUrl instead of photoURL
    if (user.avatarUrl && !imageError) {
      return (
        <img
          src={user.avatarUrl}
          alt={`${user.displayName || user.email}'s avatar`}
          className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-gray-600"
          onError={() => setImageError(true)}
        />
      );
    }

    // Fallback to colored initials
    return (
      <div
        className={`w-8 h-8 ${getAvatarColor(
          user.displayName || user.email
        )} rounded-full flex items-center justify-center text-white text-sm font-medium border border-gray-200 dark:border-gray-600`}
      >
        {getInitials(user.displayName, user.email)}
      </div>
    );
  };

  if (loading) {
    return <Loading text="Loading project settings..." />;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Project not found
        </h2>
        <button
          onClick={() => navigate("/projects")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Back to Projects
        </button>
      </div>
    );
  }

  const canManageMembers =
    project.createdBy === userProfile?.uid ||
    userProfile?.role === "admin" ||
    userProfile?.role === "manager";
  const canDelete =
    project.createdBy === userProfile?.uid || userProfile?.role === "admin";

  // Get available users (not already in project)
  const availableUsers = allUsers.filter(
    (user) => !project.members.includes(user.uid)
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-4"
        >
          ← Back to Project
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {project.emoji} {project.name} - Settings
        </h1>
      </div>

      <div className="space-y-6">
        {/* Project Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Project Settings
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Project Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter project name"
                required
              />

              <Input
                label="Project Key"
                name="key"
                value={formData.key}
                onChange={handleChange}
                placeholder="e.g., JIRA"
                maxLength={10}
                required
              />
            </div>

            <Input
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter project description"
            />

            <Select
              label="Project Icon"
              name="emoji"
              value={formData.emoji}
              onChange={handleChange}
              options={emojiOptions}
            />

            <div className="flex justify-end space-x-3">
              <Button type="submit" variant="primary" loading={saving}>
                Save Changes
              </Button>
            </div>
          </form>
        </div>

        {/* Team Members */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Team Members
          </h2>

          {/* Add Member Form */}
          {canManageMembers && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                Add Team Member
              </h3>
              <div className="flex space-x-3">
                <div className="flex-1">
                  <Select
                    label="Select User"
                    value={newMemberId}
                    onChange={(e) => setNewMemberId(e.target.value)}
                    options={[
                      { value: "", label: "Select a user to add..." },
                      ...availableUsers.map((user) => ({
                        value: user.uid,
                        label: `${user.displayName || "Unknown User"} (${
                          user.email
                        })`,
                      })),
                    ]}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    loading={addingMember}
                    onClick={handleAddMember}
                    disabled={!newMemberId}
                    className="whitespace-nowrap"
                  >
                    Add Member
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Members List */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Current Members ({users.length})
            </h4>
            {users.map((user) => (
              <div
                key={user.uid}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <UserAvatar user={user} size={8} />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {user.displayName || "Unknown User"}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </div>
                  </div>
                </div>

                {canManageMembers && user.uid !== userProfile?.uid && (
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() =>
                      handleRemoveMember(
                        user.uid,
                        user.displayName || user.email
                      )
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}

            {users.length === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                No members found in this project
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        {canDelete && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-6">
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-400 mb-2">
              Danger Zone
            </h3>
            <p className="text-red-700 dark:text-red-300 mb-4">
              Once you delete a project, there is no going back. Please be
              certain.
            </p>
            <Button
              variant="danger"
              loading={deleting}
              onClick={handleDeleteProject}
            >
              Delete Project
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectSettings;
